import { type RefObject, useEffect } from 'react';

import { bind } from 'bind-event-listener';

import type { RovoThinkingMinimizationHandle } from './RovoThinkingMinimization';

export type UseMinimizeOnScrollOptions = {
	/**
	 * Element whose scrolling collapses the thinking bar. Defaults to `window`,
	 * which is the right target when the surface scrolls with the page rather
	 * than inside its own overflow container.
	 */
	scrollContainer?: HTMLElement | Window | null;
	/**
	 * Only bind while a thinking session is on screen. Pass `false` whenever the
	 * bar is absent so background scrolling cannot minimise a bar that is about
	 * to be shown.
	 */
	isEnabled?: boolean;
	/**
	 * Returns whether the current scroll should minimize the bar. Use this to
	 * ignore application-driven scrolls that are not user intent.
	 */
	shouldMinimize?: () => boolean;
};

/**
 * Collapses a `RovoThinkingMinimization` into its pill as soon as the user
 * scrolls, so a docked thinking bar gets out of the way of the content it is
 * describing. The user can expand it again by clicking the pill.
 *
 * Shared so that every Rovo surface with a docked bar or toolbar collapses the
 * same way, rather than each one reimplementing the listener.
 */
export function useMinimizeOnScroll(
	minimizationRef: RefObject<RovoThinkingMinimizationHandle | null>,
	{ scrollContainer, isEnabled = true, shouldMinimize }: UseMinimizeOnScrollOptions = {},
): void {
	useEffect(() => {
		if (!isEnabled) {
			return;
		}

		return bind(scrollContainer ?? window, {
			type: 'scroll',
			listener: () => {
				if (shouldMinimize?.() ?? true) {
					minimizationRef.current?.minimize();
				}
			},
			// Capture runs before existing scroll detectors clear programmatic-scroll flags.
			options: { capture: true, passive: true },
		});
	}, [minimizationRef, scrollContainer, isEnabled, shouldMinimize]);
}
